<template>
  <div class="filter-entry">
    <label class="filter-entry__label" :for="inputId">{{ label }}</label>

    <!-- Text input with apply button -->
    <div v-if="type === 'text'" class="filter-entry__text-wrap" :class="{ 'filter-entry__text-wrap--active': isActive }">
      <input
        :id="inputId"
        v-model="internalValue"
        type="text"
        class="filter-entry__input"
        :placeholder="`Search by ${label.toLowerCase()}...`"
        @keyup.enter="applyFilter"
      >
      <button
        v-if="isActive"
        v-tooltip="'Clear Filter'"
        class="filter-entry__clear-btn"
        title="Clear Filter"
        @click="clearFilter"
      >
        <SVGIcon :iconName="'close'" :width="'16px'" :height="'16px'" />
      </button>
      <button
        class="filter-entry__apply-btn"
        :title="`Apply ${label} filter`"
        @click="applyFilter"
      >
        <SVGIcon :iconName="'check'" :width="'14px'" :height="'14px'" />
      </button>
    </div>

    <!-- Dropdown -->
    <div v-else-if="type === 'dropdown'" class="filter-entry__dropdown-wrap">
      <select
        :id="inputId"
        v-model="internalValue"
        class="filter-entry__input filter-entry__select"
        :class="{ 'filter-entry__select--active': isActive }"
        @change="applyFilter"
      >
        <option value="">All {{ label.toLowerCase() }}s</option>
        <option v-for="(opt, i) in options" :key="i" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
      <button
        v-if="isActive"
        v-tooltip="'Clear Filter'"
        class="filter-entry__clear-btn"
        title="Clear Filter"
        @click="clearFilter"
      >
        <SVGIcon :iconName="'close'" :width="'16px'" :height="'16px'" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import SVGIcon from '@/components/SVGIcon.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text', // 'text' | 'dropdown'
    validator: (v) => ['text', 'dropdown'].includes(v),
  },
  defaultValue: {
    type: String,
    default: '',
  },
  options: {
    // For dropdown: [{ label, value }]
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['filter']);

const inputId = `filter-entry-${props.label.toLowerCase().replace(/\s+/g, '-')}`;
const internalValue = ref(props.defaultValue);

// Sync when defaultValue changes externally (e.g. cleared by parent or card click)
watch(() => props.defaultValue, (val) => {
  internalValue.value = val;
  appliedValue.value = val;
});

// Applied filter value (last emitted)
const appliedValue = ref(props.defaultValue);
const isActive = computed(() => Boolean(appliedValue.value));

function applyFilter() {
  appliedValue.value = internalValue.value;
  emit('filter', internalValue.value);
}

function clearFilter() {
  internalValue.value = '';
  appliedValue.value = '';
  emit('filter', '');
}
</script>

<style lang="scss" scoped>
.filter-entry {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
  flex: 1;
  max-width: 280px;
}

.filter-entry__label {
  font-size: 0.78rem;
  font-weight: 600;
  @include themify() {
    color: #555;
  }
}

.filter-entry__text-wrap {
  display: flex;
  align-items: center;
  gap: 0;
  @include themify() {
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    background: #ffffff;
  }

  &:focus-within {
    @include themify() {
      border-color: #250E81;
    }
  }
}

.filter-entry__input {
  height: 32px;
  padding: 0 8px;
  font-size: 0.85rem;
  outline: none;
  border: none;
  background: transparent;
  flex: 1;
  min-width: 0;
  @include themify() {
    color: #333;
  }
}

.filter-entry__select {
  width: 100%;
  padding: 0 8px;
  cursor: pointer;
  border-radius: 4px;
  @include themify() {
    border: 1px solid #d0d0d0;
    background: #ffffff;
    color: #333;
  }
  &:focus {
    outline: none;
    @include themify() {
      border-color: #250E81;
    }
  }
}

.filter-entry__text-wrap--active {
  @include themify() {
    border-color: #d0d0d0;
    border-bottom: 2px solid #dc2626;
  }
}

.filter-entry__dropdown-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-entry__dropdown-wrap .filter-entry__select {
  flex: 1;
  min-width: 0;
}

.filter-entry__select--active {
  @include themify() {
    border-bottom: 2px solid #dc2626;
  }
}

.filter-entry__clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  @include themify() {
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
  }
  &:hover {
    @include themify() {
      background: rgba(220, 38, 38, 0.2);
    }
  }
}

.filter-entry__clear-btn--inline {
  position: absolute;
  right: 6px;
}

.filter-entry__apply-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 0 4px 4px 0;
  @include themify() {
    color: #555;
  }
  &:hover {
    @include themify() {
      background: rgba(0, 0, 0, 0.06);
      color: #250E81;
    }
  }
}
</style>
